import { Component, OnInit, OnDestroy, Output, ViewChild, TemplateRef, AfterViewInit, Input } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { trigger, style, transition, animate } from '@angular/animations';

import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { TargetingSearchesService } from '../../../services/targeting-searches.service';
import { JournodexEnumsService } from '../../../../Core/services/journodex-enums.service';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { IncludeOrExclude } from '../../../../../Common/Dialogs/services/data-select-dialog.service.interface';
import { JournodexEntityFactory } from '../../../factories/journodex-entity.factory';
import { OutletModel } from '../../../models/outlet.model';
import { TargetingSearch } from '../../../models/targeting-search.model';
import { AppearCondition, AutocompleteCustomOption } from '../../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { ContactModel } from '../../../../../Common/TargetingShared/model/contact.model';
import { InputValidationModel } from '../../../../../Common/Forms/models/input-validation.model';
import { TargetingGeographyService } from '../../../../../Common/TargetingShared/services/targeting-geography-service';
import { AutocompleteChipsOptions } from '../../../../../Common/Forms/components/AutocompleteChips/autocomplete-chips.component';
import { IncludeExcludeModel } from '../../../../../Common/Misc/models/include-exclude-model';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { JournodexSubjectParentModel } from '../../../../../Common/Journodex/models/journodex.subject.model';
import { TargetingSearchRegionSelectDialogService } from '../../../../../Common/TargetingShared/services/targeting-search-region-select-dialog.service';
import { DataSelectDialogModel } from '../../../../../Common/Dialogs/models/data-select-dialog.model';
import { JournodexGeographyModel } from '../../../../../Common/Journodex/models/journodex-geography.model';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { DataSelectTakeoverPanelComponent } from '../../../../../Common/Dialogs/components/DataSelectTakeoverPanel/data-select-takeover-panel.component';
import { JobRoleModel } from '../../../../Core/models/job-role.model';
import type { LanguageModel } from '../../../../../Core/Localization/models/language.model';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { TargetingSearchLanguageSelectDialogService } from '../../../../../Common/TargetingShared/services/targeting-search-language-select-dialog.service';
import { IncludeExcludeBrowseAllOptions } from '../../IncludeExcludeSubjectChips/include-exclude-subject-chips.component';
import { JobRoleSelectDialogService } from '../../../services/jobrole-select-dialog.service';
import { MediaMarket } from '../../../../Core/models/media-market.model';
import { TargetingSearchFullModel } from '../../../models/targeting-search-full.model';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { DropdownOptionModel } from '../../../../../Common/Forms/models/dropdown-option.model';
import { ConfirmDialogComponent } from '../../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { SubjectDialogComponent } from '../SubjectDialog/subject-dialog.component';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';
import { MediaMarketDialogComponent } from '../MediaMarketDialog/media-market-dialog.component';
import { BrowseAllType } from '../../IncludeExcludeMediaMarketChips/include-exclude-media-market-chips.component';

enum RegionKey {
    geographies = 'geographies',
    areasOfCoverage = 'employment.areasOfCoverage'
}

enum SubjectKey {
    subjects = 'subjects',
    outletSubjects = 'employment.outletSubjects'
}

enum CopilotQueryType {
    distribution,
    text
}

/**
 * This component uses reactive forms.
 * Data changes in this form are passed up to the parent via the output 'valueChanges'
 * 'formReady' output allows the parent form group to append this contact form.
 * This is useful for validation only and should not be used to communicate data.
 */
@Component({
    selector: 'agpr-contact-search',
    templateUrl: './contact-search.component.html',
    styleUrls: ['./contact-search.component.scss'],
    providers: [
        TargetingSearchRegionSelectDialogService,
        TargetingSearchLanguageSelectDialogService,
        JobRoleSelectDialogService,
    ],
    animations: [
        trigger('panelContent', [
            transition(':enter', [
                style({ opacity: 0, height: 0, paddingTop: 0, paddingBottom: 0 }),
                animate('400ms ease-in', style({ opacity: 1, height: '*', paddingTop: '20px', paddingBottom: '20px' })),
            ]),
            transition(':leave', [
                animate('400ms ease-in', style({ opacity: 0, height: 0, paddingTop: 0, paddingBottom: 0 }))
            ])
        ])
    ],
    standalone: false
})
export class ContactSearchComponent implements OnInit, AfterViewInit, OnDestroy {

    contactForm: FormGroup = new FormGroup({
        contacts: new FormControl(<ContactModel[]>[]),
        keywords: new FormControl(new IncludeExcludeModel<string>()),
        subjects: new FormControl(new IncludeExcludeModel<JournodexSubjectParentModel>()),
        geographies: new FormControl(new IncludeExcludeModel<JournodexGeographyModel>(), [JournodexGeographyModel.geographyValidator()]),
        jobTitles: new FormControl(new IncludeExcludeModel<string>()),
        jobRoles: new FormControl(new IncludeExcludeModel<JobRoleModel>()),
        languages: new FormControl(new IncludeExcludeModel<LanguageModel>()),
        employment: new FormGroup({
            outlets: new FormControl(<OutletModel[]>[]),
            outletKeywords: new FormControl(new IncludeExcludeModel<string>()),
            outletSubjects: new FormControl(new IncludeExcludeModel<JournodexSubjectParentModel>()),
            areasOfCoverage: new FormControl(new IncludeExcludeModel<JournodexGeographyModel>()),
            mediaMarkets: new FormControl(new IncludeExcludeModel<MediaMarket>()),
        })
    });

    @Input() set contactData(search: TargetingSearchFullModel) {
        this.contactForm.setValue({
            contacts: search.contacts,
            keywords: search.keywords,
            subjects: search.subjects,
            geographies: search.geographies,
            jobTitles: search.jobTitles,
            jobRoles: search.jobRoles,
            languages: search.languages,
            employment: {
                outlets: search.outlets,
                outletKeywords: search.outletKeywords,
                outletSubjects: search.outletSubjects,
                areasOfCoverage: search.areasOfCoverage,
                mediaMarkets: search.mediaMarkets
            }
        } ,{ emitEvent: false, onlySelf: true});
    }

    @Output() formReady = of(this.contactForm);

    @Output() valueChange = this.contactForm.valueChanges.pipe(
        // @hack - fixes timing issues between valueChanges, patchValue
        debounceTime(300),
        map(
            (formValue): Partial<TargetingSearchFullModel> => ({
                contacts: formValue.contacts,
                keywords: formValue.keywords,
                subjects: formValue.subjects,
                geographies: formValue.geographies,
                jobTitles: formValue.jobTitles,
                jobRoles: formValue.jobRoles,
                languages: formValue.languages,
                outlets: formValue.employment.outlets,
                outletKeywords: formValue.employment.outletKeywords,
                outletSubjects: formValue.employment.outletSubjects,
                areasOfCoverage: formValue.employment.areasOfCoverage,
                mediaMarkets: formValue.employment.mediaMarkets,
            })
        ),
    );

    @ViewChild('noContentMessageTemplate') noContentMessageTemplate: TemplateRef<any>;


    hasMediaMarkets: boolean = false;
    noContentMessages: { [key: string]: AutocompleteCustomOption } = {};
    previewLoading: boolean = false;
    previewCount: number = 0;

    hasAIHelper: boolean = false;
    prCopilotLoading: boolean = false;
    prCopilotExpanded: boolean = false;
    prCopilotQueryType: CopilotQueryType = CopilotQueryType.distribution;

    distributionSuggestions: Observable<DropdownOptionModel[]>;
    selectedDistribution: DropdownOptionModel | null;

    prCopilotTextQuery: string = '';

    overlayContainer: Element;

    readonly id = 'targetingContactSearch';
    readonly regionKey = RegionKey;
    readonly subjectKey = SubjectKey;
    readonly messageSeverity = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly copilotQueryTypes = CopilotQueryType;
    readonly splitOnPattern: RegExp = /(")?,(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)\1 */g;
    readonly noContentMessageTypes = ['region', 'jobRole', 'language', 'targetAudienceRegion'];
    readonly MAX_SEARCH_SIZE = TargetingSearch.MAX_SEARCH_SIZE;
    readonly keywordValidators = [
        new InputValidationModel({
            errorKey: 'Monitoring.keyWords.minimumKeywordCharacterLength',
            isValid: (input: string): boolean => {
                return input.trim().length > 1;
            }
        }),
        // validation for single keywords
        new InputValidationModel({
            errorKey: 'Monitoring.keywords.maximumKeywordCharacterLength',
            isValid: (input: string): boolean => {
                return input.trim().length < 3072;
            }
        }),
        // validation for the total length of all keywords
        new InputValidationModel({
            errorKey: 'Monitoring.keywords.maximumKeywordCharacterLength',
            isValid: (input: string): boolean => {
                const totalLength = [
                    ...(this.keywords.include || []),
                    ...(this.keywords.exclude || []),
                    ...(this.keywords.includeAll || []),
                    input
                ]
                    .map(str => str.trim())
                    .join('')
                    .length;

                return totalLength < 3072;
            }
        })
    ];
    readonly minCharacterValidator = new InputValidationModel({
        errorKey: 'Monitoring.minLength.error.message',
        isValid: (input: string): boolean => {
            return input.trim().length > 1;
        }
    });
    readonly contactNameAutocompleteChipsOptions: AutocompleteChipsOptions = {
        customOptions: [
            {
                label: 'Discover.targeting.searchFieldLabels.names.searchFor',
                action: (searchString: string) => {
                    const contacts: ContactModel[] = this.contactForm.get('contacts').value;

                    if (searchString?.trim()?.length < 3 || contacts.find(x => x.name === searchString)) {
                        return;
                    }

                    const newContact = this.journodexEntityFactory.createEmptyContact();
                    newContact.name = searchString;

                    contacts.push(newContact);
                    this.contactForm.patchValue({ 'contacts' : contacts });
                },
                appearCondition: AppearCondition.always,
                clearOnSelect: true
            },
        ],
        forceSelection: false,
        suggestionsFetcher: this.targetingSearchesService.contactSearcher.bind(this.targetingSearchesService)
    };
    readonly outletNameAutocompleteOptions: AutocompleteChipsOptions = {
        customOptions: [
            {
                label: 'Discover.targeting.searchFieldLabels.names.searchFor',
                action: (searchString: string) => {
                    const outlets: OutletModel[] = this.contactForm.get('employment.outlets').value;

                    if (searchString?.trim()?.length < 3 || outlets.find(x => x.name === searchString)) {
                        return;
                    }

                    const newOutlet = this.journodexEntityFactory.createEmptyOutlet();
                    newOutlet.name = searchString;

                    outlets.push(newOutlet);
                    this.contactForm.get('employment').patchValue({'outlets' : outlets});
                },
                appearCondition: AppearCondition.always,
                clearOnSelect: true
            },
        ],
        forceSelection: false,
        suggestionsFetcher: this.targetingSearchesService.outletSearcher.bind(this.targetingSearchesService),
    };

    private subscriptions: Subscription[] = [];

    constructor(
        public targetingSearchesService: TargetingSearchesService,
        public journodexEnumsService: JournodexEnumsService,
        public localizationService: LocalizationService,
        public targetingGeographyService: TargetingGeographyService,
        protected regionSelectDialogService: TargetingSearchRegionSelectDialogService,
        protected coreDialogService: CoreDialogService,
        protected journodexEntityFactory: JournodexEntityFactory,
        protected languageSelectDialogService: TargetingSearchLanguageSelectDialogService,
        protected jobRoleSelectDialogService: JobRoleSelectDialogService,
        protected messageService: MessageService,
        protected currentUserStateService: CurrentUserStateService
    ) { }

    ngOnInit() {
        this.overlayContainer = document.getElementById('main-content');
        this.subscriptions.push(
            this.journodexEnumsService.getAllMediaMarkets().subscribe(mediaMarkets => {
                this.hasMediaMarkets = mediaMarkets.length > 0;
            }),
            this.targetingSearchesService.getPreviewCount().subscribe(count => {
                if (!count) {
                    this.previewCount = 0;
                    return;
                }

                this.previewCount = count;
            }),
            this.targetingSearchesService.getPreviewCountLoading().subscribe(loading => {
                this.previewLoading = loading;
            }),
            this.currentUserStateService.accountHaveAiPermissions().subscribe(showAiHelper => {
                this.hasAIHelper = showAiHelper;
            })
        );
        this.distributionSuggestions = this.targetingSearchesService.getDistributionSummaries().pipe(
            map(summaries => {
                return summaries.map(summary => {
                    return new DropdownOptionModel(summary.name, summary.id);
                });
            })
        );
    }

    ngAfterViewInit(): void {
        requestAnimationFrame(() => {
            this.prepareNoContentMessages();
        });
    }

    get keywords() {
        return this.contactForm.get('keywords').value;
    }

    get outletKeywords() {
        return this.contactForm.get('employment.outletKeywords').value;
    }

    get form() {
        return this.contactForm.controls;
    }

    get geographies(): FormGroup {
        return this.contactForm.get('geographies') as FormGroup;
    }

    openMediaMarketDialog(browseAll: BrowseAllType) {
        const io = {
            inputs: {
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                includeExclude: browseAll.includeOrExclude,
                selectedMarkets: this.contactForm.get('employment.mediaMarkets').value[browseAll.includeOrExclude],
                scrollToChildId: browseAll.childId || null
            },
            outputs: {
                dialogClose: (selectedMediaMarkets: MediaMarket[]) => {
                    if (selectedMediaMarkets === null) {
                        return;
                    }

                    const newMediaMarket: IncludeExcludeModel<MediaMarket> = this.contactForm.get('employment.mediaMarkets').value;
                    newMediaMarket[browseAll.includeOrExclude] = selectedMediaMarkets;

                    this.contactForm.get('employment').patchValue({'mediaMarkets': newMediaMarket});
                }
            }
        };
        this.coreDialogService.open(MediaMarketDialogComponent, io);
    }

    showJobRoleBrowser(includeOrExclude: IncludeOrExclude) {
        const jobRoles = this.contactForm.get('jobRoles').value[includeOrExclude];
        this.jobRoleSelectDialogService.prepareModelData(includeOrExclude, jobRoles);

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io: DataSelectDialogModel = {
            inputs: {
                id: 'targetingSearch.jobRoleSelectDialog',
                dialogService: this.jobRoleSelectDialogService,
                dialogHeader: 'Discover.dialogs.browsers.jobrole.header',
                dialogMessage: 'Discover.dialogs.browsers.jobrole.message',
                leftColumnHeader: 'Discover.dialogs.browsers.jobrole.available',
                rightColumnHeader: 'Discover.dialogs.browsers.jobrole.selected',
                filterDataType: {
                    type: 'Discover.dialogs.browsers.jobrole.header',
                    noResultsLocalizationString: 'Common.forms.treeList.noResultsFeminine'
                },
                overlayContainer: this.overlayContainer,
                animationTrigger
            },
            outputs: {
                dialogClose: (selectedJobRoles: JobRoleModel[]) => {
                    if (!selectedJobRoles) {
                        selectedJobRoles = [];
                    }

                    const newJobRole: IncludeExcludeModel<JobRoleModel> = this.contactForm.get('jobRoles').value;
                    newJobRole[includeOrExclude] = selectedJobRoles;

                    // @hack emitEvent false prevents triggering 2 emits on the valueChanges
                    this.contactForm.patchValue({'jobRoles': newJobRole }, { emitEvent: false });
                }
            }
        };
        this.coreDialogService.open(DataSelectTakeoverPanelComponent, io);
    }

    showSubjectsBrowser(property: SubjectKey, options: IncludeExcludeBrowseAllOptions) {
        const subjects = this.contactForm.get(property).value[options.includeOrExclude];

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                selectedSubjects: subjects || [],
                initialFilterString: options?.options?.query,
                scrollToParentId: options?.options?.parent?.id
            },
            outputs: {
                dialogClose: (selectedSubjects: JournodexSubjectParentModel[]) => {
                    if (selectedSubjects === null) {
                        return;
                    }

                    const newSubject: IncludeExcludeModel<JournodexSubjectParentModel> = this.contactForm.get(property).value;
                    newSubject[options.includeOrExclude] = selectedSubjects;

                    this.contactForm.patchValue({ [property]: newSubject});
                }
            }
        };

        this.coreDialogService.open(SubjectDialogComponent, io);
    }

    showLanguageBrowser(includeOrExclude: IncludeOrExclude) {
        const languages = this.contactForm.get('languages').value[includeOrExclude];
        this.languageSelectDialogService.prepareModelData(includeOrExclude, languages);

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io: DataSelectDialogModel = {
            inputs: {
                id: 'targetingSearch.languageSelectDialog',
                dialogService: this.languageSelectDialogService,
                dialogHeader: 'Monitoring.result.languages',
                dialogMessage: 'Monitoring.result.selectLanguagesMsg',
                leftColumnHeader: 'Monitoring.result.availableLanguages',
                rightColumnHeader: 'Monitoring.result.selectedLanguages',
                overlayContainer: this.overlayContainer,
                filterDataType: {
                    type: 'Monitoring.result.languages',
                    noResultsLocalizationString: 'Common.forms.treeList.noResultsFeminine'
                },
                animationTrigger
            },
            outputs: {
                dialogClose: (selectedLanguages: LanguageModel[]) => {
                    if (!selectedLanguages) {
                        selectedLanguages = [];
                    }
                    const newLang: IncludeExcludeModel<LanguageModel> = this.contactForm.get('languages').value;
                    newLang[includeOrExclude] = selectedLanguages;

                    this.contactForm.patchValue({'languages': newLang});
                }
            }
        };
        this.coreDialogService.open(DataSelectTakeoverPanelComponent, io);
    }


    showRegionBrowser(property: RegionKey, includeOrExclude: IncludeOrExclude) {
        const geographies = this.contactForm.get(property).value[includeOrExclude];
        this.regionSelectDialogService.prepareModelData(includeOrExclude, geographies);

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io: DataSelectDialogModel = {
            inputs: {
                id: 'targetingSearch.regionSelectDialog',
                dialogService: this.regionSelectDialogService,
                propagateSelection: (property === RegionKey.areasOfCoverage) ? false : true,
                dialogHeader: 'Monitoring.search.regions',
                dialogMessage: 'Monitoring.search.selectedRegionsMsg',
                leftColumnHeader: 'Monitoring.search.availableRegions',
                rightColumnHeader: 'Monitoring.search.selectedRegions',
                lockMessage: 'Discover.dialogs.browsers.lockedGeo',
                overlayContainer: this.overlayContainer,
                animationTrigger
            },
            outputs: {
                dialogClose: (selectedRegions: JournodexGeographyModel[]) => {
                    if (!selectedRegions) {
                        selectedRegions = [];
                    }
                    const newGeo: IncludeExcludeModel<JournodexGeographyModel> = this.contactForm.get(property).value;
                    newGeo[includeOrExclude] = selectedRegions;

                    this.contactForm.patchValue({ [property]: newGeo});
                }
            }
        };
        this.coreDialogService.open(DataSelectTakeoverPanelComponent, io);
    }

    prepareNoContentMessages(): void {
        this.noContentMessageTypes.forEach(searchType => {
            const customOption: AutocompleteCustomOption = {
                appearCondition: AppearCondition.noSuggestions,
                clearOnSelect: false,
                action: () => {},
                labelTemplate: this.noContentMessageTemplate,
                labelTemplateVariable: {
                    severity: messageSeverityEnum.noContent,
                    noContentMessageLabel: `Discover.targeting.searchFieldLabels.noContentMessage.${searchType}`
                }
            };

            this.noContentMessages[searchType] = customOption;
        });
    }

    getRecommendedRecipientsQuery() {
        this.prCopilotLoading = true;
        let request: Observable<boolean>;
        if (this.prCopilotQueryType === CopilotQueryType.distribution) {
            if (this.selectedDistribution === null) {
                return;
            }
            request = this.targetingSearchesService.getRecommendedRecipientsQueryByDistribution(this.selectedDistribution.value);
        } else {
            request = this.targetingSearchesService.getRecommendedRecipientsQueryByText(this.prCopilotTextQuery);
        }
        request.subscribe({
            next: (result) => {
                this.prCopilotLoading = false;
                if (result) {
                    this.prCopilotExpanded = false;
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Discover.targeting.search.prCopilot.success.summary',
                        detail: 'Discover.targeting.search.prCopilot.success.detail'
                    });
                } else {
                    const failureDialogIO = {
                        inputs: {
                            heading: 'Discover.targeting.search.prCopilot.failure.heading',
                            message: 'Discover.targeting.search.prCopilot.failure.message',
                            okLabel: 'Discover.targeting.search.prCopilot.acknowledge'
                        }
                    };
                    this.coreDialogService.open(ConfirmDialogComponent, failureDialogIO);
                }
            },
            error: (err) => {
                this.prCopilotLoading = false;
                const errorDialogIO = {
                    inputs: {
                        heading: 'Discover.targeting.search.prCopilot.error.heading',
                        message: 'Discover.targeting.search.prCopilot.error.message',
                        okLabel: 'Discover.targeting.search.prCopilot.acknowledge'
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, errorDialogIO);
            }
        });
    }

    distributionSelected(item: DropdownOptionModel|null) {
        this.selectedDistribution = item;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
